import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/lib/theme-provider";

interface ThemeToggleProps {
  variant?: "icon" | "switch";
  className?: string;
}

export function ThemeToggle({
  variant = "icon",
  className = "",
}: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  // Resolve effective theme (system → actual preference)
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const toggleTheme = () => {
    setTheme(isDark ? "light" : "dark");
  };

  if (variant === "switch") {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <Label htmlFor="theme-toggle" className="cursor-pointer">
          <div className="flex items-center gap-2">
            {isDark ? (
              <Moon className="h-4 w-4 text-indigo-400" />
            ) : (
              <Sun className="h-4 w-4 text-amber-500" />
            )}
            <span>{isDark ? "Dark" : "Light"} Mode</span>
          </div>
        </Label>
        <Switch
          id="theme-toggle"
          checked={isDark}
          onCheckedChange={() => toggleTheme()}
        />
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      className={className}
      title={`Switch to ${isDark ? "light" : "dark"} theme`}
    >
      {isDark ? (
        <Sun className="h-5 w-5 text-amber-500" />
      ) : (
        <Moon className="h-5 w-5 text-slate-700" />
      )}
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}
